import { z } from 'zod';

/**
 * Admin two-factor authentication (ADR-014) — mandatory TOTP second factor
 * at admin-dashboard login, self-service setup/disable at
 * Admin → Settings → Security. Never applies to therapist-portal or
 * mobile; see auth.ts for the login-flow contracts (LoginResponseSchema's
 * `twoFactorRequired` branch, VerifyTwoFactorRequestSchema).
 */

export const TwoFactorStatusResponseSchema = z.object({
  enabled: z.boolean(),
  confirmedAt: z.string().datetime().nullable(),
});
export type TwoFactorStatusResponse = z.infer<typeof TwoFactorStatusResponseSchema>;

/**
 * The secret is shown once, in plain text, alongside the QR code — for
 * manual entry when scanning isn't possible. Nothing here is a bearer
 * credential by itself (2FA isn't enabled until POST /confirm succeeds).
 */
export const TwoFactorSetupResponseSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  /** data:image/png;base64,... — rendered directly in an <img> tag. */
  qrCodeDataUrl: z.string(),
});
export type TwoFactorSetupResponse = z.infer<typeof TwoFactorSetupResponseSchema>;

export const TwoFactorConfirmRequestSchema = z.object({
  code: z.string().length(6),
});
export type TwoFactorConfirmRequest = z.infer<typeof TwoFactorConfirmRequestSchema>;

/** Recovery codes are returned ONLY here, ONLY once — never retrievable again after this response. */
export const TwoFactorConfirmResponseSchema = z.object({
  recoveryCodes: z.array(z.string()),
});
export type TwoFactorConfirmResponse = z.infer<typeof TwoFactorConfirmResponseSchema>;

/** Disabling requires both the account password AND a valid second factor (current TOTP code or an unused recovery code). */
export const TwoFactorDisableRequestSchema = z.object({
  password: z.string().min(1),
  code: z.string().min(6).max(11),
});
export type TwoFactorDisableRequest = z.infer<typeof TwoFactorDisableRequestSchema>;
